import React, { forwardRef, useMemo, useEffect } from 'react';
import PropTypes from '../../../node_modules/prop-types/index.js';
import Icon from '../Icon/Icon.js';
import i18n from '../../lang/i18n/index.js';
import { rem, noop } from '../../utils/functions.js';
import { DATE_PART, FOCUS_ELEMENT } from './constants.js';
import { ToggleButton } from './styledComponents.js';
import { isDisabledMonth, shiftDate, clampMonth } from './utils.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';

var getAriaLabel = function getAriaLabel(months, month, year, inc) {
  var monthLabel = months[clampMonth(month, inc)];
  var yearLabel;

  if (month === 0 && inc === -1) {
    yearLabel = year - 1;
  } else if (month === months.length - 1 && inc === 1) {
    yearLabel = year + 1;
  } else {
    yearLabel = year;
  }

  return "".concat(monthLabel, " ").concat(yearLabel);
};

var MonthToggle = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var inc = _ref.inc,
      disabledDays = _ref.disabledDays,
      months = _ref.months,
      focusedDate = _ref.focusedDate,
      shift = _ref.shift,
      title = _ref.title,
      tabKeyHandler = _ref.tabKeyHandler,
      setFocusedElement = _ref.setFocusedElement,
      focusedDayRef = _ref.focusedDayRef;
  var lang = useLang();
  var theme = useTheme();
  var before = disabledDays.before,
      after = disabledDays.after;
  var month = focusedDate.month,
      year = focusedDate.year;
  var monthTitle = useMemo(function () {
    if (inc === -1) {
      return title || i18n[lang].datePicker.togglePreviousMonthTitle;
    }

    if (inc === 1) {
      return title || i18n[lang].datePicker.toggleNextMonthTitle;
    }

    return '';
  }, [lang, title, inc]);
  var disabled = isDisabledMonth(shiftDate(focusedDate, inc, DATE_PART.MONTH, false), before, after);
  useEffect(function () {
    if (disabled && focusedDayRef && focusedDayRef.current) {
      setFocusedElement(FOCUS_ELEMENT.DAY);
      focusedDayRef.current.focus();
    }
  }, [disabled, focusedDayRef, setFocusedElement]);
  var handleClick = disabled ? noop : function () {
    return shift(inc, DATE_PART.MONTH, true, true);
  };
  var iconColor = disabled ? theme.colorPalette.neutralColors.light.shade_dark_3 : theme.colorPalette.neutralColors.dark.shade_light_1;
  var iconName = '';

  if (inc === -1) {
    iconName = 'arrowleft';
  } else if (inc === 1) {
    iconName = 'arrowright';
  } else ;

  return /*#__PURE__*/React.createElement(ToggleButton, {
    type: "button",
    "aria-label": getAriaLabel(months, month, year, inc),
    onClick: handleClick,
    disabled: disabled,
    theme: theme,
    ref: ref,
    onKeyDown: tabKeyHandler
  }, /*#__PURE__*/React.createElement(Icon, {
    alt: monthTitle,
    color: iconColor,
    fill: iconColor,
    manulifeName: iconName,
    width: rem(12),
    height: rem(12)
  }));
});
process.env.NODE_ENV !== "production" ? MonthToggle.propTypes = {
  inc: PropTypes.oneOf([-1, 1]).isRequired,
  disabledDays: PropTypes.shape({
    before: PropTypes.instanceOf(Date),
    after: PropTypes.instanceOf(Date)
  }).isRequired,
  months: PropTypes.arrayOf(PropTypes.string).isRequired,
  focusedDate: PropTypes.shape({
    day: PropTypes.number,
    month: PropTypes.number,
    year: PropTypes.number
  }).isRequired,
  shift: PropTypes.func.isRequired,
  title: PropTypes.string,
  tabKeyHandler: PropTypes.func.isRequired,
  setFocusedElement: PropTypes.func.isRequired,
  focusedDayRef: PropTypes.object
} : void 0;
MonthToggle.defaultProps = {
  title: undefined,
  focusedDayRef: {
    current: null
  }
};
process.env.NODE_ENV !== "production" ? MonthToggle.displayName = 'MonthToggle' : void 0;
var MonthToggle$1 = MonthToggle;

export { MonthToggle$1 as default };
